/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads -- readViewUserField_servicedeskCustomerServiceCustomDetails is only defined and used by the user field */
import React, { useMemo, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ff } from '@atlassian/jira-feature-flagging';
import type { readOnlyCustomDetail_servicedeskCustomerServiceCustomDetails$key } from '@atlassian/jira-relay/src/__generated__/readOnlyCustomDetail_servicedeskCustomerServiceCustomDetails.graphql';
import {
	BooleanFieldReadView,
	DateFieldReadView,
	EmailFieldReadView,
	MultiSelectFieldReadView,
	NumberFieldReadView,
	PhoneNumberFieldReadView,
	SingleSelectFieldReadView,
	TextFieldReadView,
	UrlFieldReadView,
	UserFieldReadView,
} from '../../common/ui/field';
import { DefaultLayout } from '../../common/ui/layout';

export const ReadOnlyCustomDetail: FC<{
	customDetailRef: readOnlyCustomDetail_servicedeskCustomerServiceCustomDetails$key;
	isInline?: boolean;
	popup?: {
		renderContent: () => React.ReactNode;
	};
	Layout?: typeof DefaultLayout;
	testId?: string;
}> = ({ customDetailRef, isInline, popup, Layout = DefaultLayout, testId }) => {
	const data = useFragment(
		graphql`
			fragment readOnlyCustomDetail_servicedeskCustomerServiceCustomDetails on CustomerServiceCustomDetailValue
			@argumentDefinitions(includePlatformValue: { type: "Boolean!" }) {
				name @required(action: THROW)
				value
				values
				type {
					name @required(action: THROW)
					options
				}
				config {
					styleConfiguration {
						options {
							optionValue
							style {
								backgroundColour
							}
						}
					}
				}
				...readViewUserField_servicedeskCustomerServiceCustomDetails
					@arguments(includePlatformValue: $includePlatformValue)
			}
		`,
		customDetailRef,
	);
	const { name, value, values, type, config } = data;

	const CustomDetailField = useMemo(() => {
		if (type.name === 'USER' && !ff('csm-shipit-58-user-custom-detail-type_be1zw')) {
			return null;
		}

		switch (type.name) {
			case 'TEXT':
				return TextFieldReadView;
			case 'NUMBER':
				return NumberFieldReadView;
			case 'EMAIL':
				return EmailFieldReadView;
			case 'URL':
				return UrlFieldReadView;
			case 'DATE':
				return DateFieldReadView;
			case 'PHONE':
				return PhoneNumberFieldReadView;
			case 'BOOLEAN':
				return BooleanFieldReadView;
			case 'SELECT':
				return SingleSelectFieldReadView;
			case 'MULTISELECT':
				return MultiSelectFieldReadView;
			case 'USER':
				return UserFieldReadView;
			default:
				return null;
		}
	}, [type.name]);

	if (!CustomDetailField) {
		return <></>;
	}

	const formattedOptions = type.options?.map((option) => {
		const color = config?.styleConfiguration?.options?.find(
			({ optionValue }) => optionValue === option,
		)?.style?.backgroundColour;
		return {
			label: option,
			value: option,
			color,
		};
	});

	return (
		<Layout isInline={isInline} popup={popup} label={name} testId={testId}>
			{({ getValueContainer }) => (
				<CustomDetailField
					value={value}
					values={values}
					userFieldRef={data}
					formattedOptions={formattedOptions}
					getValueContainer={getValueContainer}
				/>
			)}
		</Layout>
	);
};
